import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useStrategyFilters } from '@/hooks/useStrategyFilters';
import { useStrategies, useStrategyCategories } from '@/hooks/useStrategies';
import StrategiesTable from '@/components/StrategiesTable';
import { Pagination } from '@/components/Pagination';
import type { StrategyFilters } from '@/types/strategy';

export default function StrategiesPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useStrategyFilters();
  const { data, isLoading, isError, error } = useStrategies(filters);
  const { data: categories } = useStrategyCategories();

  useEffect(() => {
    document.title = 'Strategies | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load strategies');
    }
  }, [isError, error]);

  const handleSort = useCallback(
    (column: StrategyFilters['sortBy']) => {
      if (filters.sortBy === column) {
        updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        updateFilter('sortBy', column);
        updateFilter('sortOrder', 'asc');
      }
    },
    [filters.sortBy, filters.sortOrder, updateFilter]
  );

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-pine-display font-display text-pine-ink">Strategies</h1>
            <p className="text-pine-body text-pine-ink-soft mt-1">
              Manage strategies for the Check in Moment feature
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/strategies/new')}
            className="admin-button-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Strategy</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-4">
          <select
            value={filters.categoryId}
            onChange={(e) => updateFilter('categoryId', e.target.value)}
            className="px-4 py-2 rounded-lg border border-pine-line bg-white text-pine-ink"
          >
            <option value="">All Categories</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as StrategyFilters['status'])}
            className="px-4 py-2 rounded-lg border border-pine-line bg-white text-pine-ink"
          >
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-pine-ink-soft hover:text-pine-ink underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* Error State */}
      {isError && (
        <div className="admin-card p-6 border border-red-500/20 bg-red-500/5">
          <p className="text-red-400">Failed to load strategies. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StrategiesTable
            strategies={data?.items || []}
            isLoading={isLoading}
            isEmpty={!isLoading && data?.total === 0 && !hasActiveFilters}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleSort}
          />
        </motion.div>
      )}

      {/* Pagination */}
      {!isError && data && totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          totalItems={data.total}
          pageSize={filters.pageSize}
          onPageChange={(page) => updateFilter('page', page)}
        />
      )}
    </div>
  );
}
